import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import * as waitlist from "@/api/waitlist";

// ═══════════════════════════════════════════════════════════════════
// Who is waiting.
//
// This page exists because the list had no read path at all. People joined
// from wedeshi.io, the rows were written correctly, and there was no way to
// see them short of opening the database by hand. Two real closed-beta
// signups sat unseen for four days.
//
// Admin only. Every row is a real email address belonging to somebody with no
// account — the most sensitive data in the API, and the group with the least
// recourse if it leaks.
// ═══════════════════════════════════════════════════════════════════

export default function WaitlistLab() {
  const [source, setSource] = useState<"" | "landing" | "business" | "organiser">("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["waitlist", source],
    queryFn: () => waitlist.listWaitlist(source ? { source, limit: 200 } : { limit: 200 }),
    retry: false,
  });

  const rows = data?.data.signups ?? [];
  const total = data?.data.total ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Waitlist"
        subtitle="People who gave an email before the product existed."
        breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Waitlist" }]}
      />

      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <div className="flex items-center gap-3 flex-wrap mb-4">
          <div>
            <div className="text-3xl font-semibold text-gray-900 tabular-nums">{total}</div>
            <div className="text-xs text-gray-500">on the list</div>
          </div>

          <select
            value={source}
            onChange={(e) => setSource(e.target.value as typeof source)}
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm ml-auto"
          >
            <option value="">every door</option>
            <option value="landing">landing — a member</option>
            <option value="business">business — an owner</option>
            <option value="organiser">organiser — an event host</option>
          </select>

          {/*
            A plain link, not a fetch. The browser must save this as a file,
            and the response is text rather than the usual envelope.
          */}
          <a href={waitlist.waitlistCsvUrl(source || undefined)} download>
            <Button size="sm" variant="secondary">
              Download CSV
            </Button>
          </a>
        </div>

        {isLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : error ? (
          <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded px-3 py-2">
            This list is admin-only, so an ordinary member is refused — the guard
            working, not an outage. The server said:{" "}
            <em>{error instanceof Error ? error.message : "forbidden"}</em>
          </p>
        ) : !rows.length ? (
          <p className="text-sm text-gray-500">Nobody yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-3">Joined</th>
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3">Email</th>
                  <th className="py-2 pr-3">City</th>
                  <th className="py-2 pr-3">From</th>
                  <th className="py-2">Door</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.id} className="border-b border-gray-100">
                    <td className="py-2 pr-3 text-gray-500 whitespace-nowrap tabular-nums">
                      {new Date(s.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-2 pr-3 text-gray-900">{s.fullName}</td>
                    <td className="py-2 pr-3 text-gray-700">{s.email}</td>
                    <td className="py-2 pr-3 text-gray-700">{s.city}</td>
                    <td className="py-2 pr-3 text-gray-500">{s.originState ?? "—"}</td>
                    <td className="py-2">
                      <Badge variant={s.source === "business" ? "purple" : "gray"}>
                        {s.source}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
